import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_MY_SUPPORT_TICKETS } from '../../graphql/support';
import useMainStore from '../../store/mainState';
import GButton from '../../components/ui/base/GButton';
import CreateTicketForm from './CreateTicketForm';
import TicketDetail from './TicketDetail';
import TicketList from './TicketList';

export type SupportTicket = {
    id: number;
    environmentId: number;
    title: string;
    description: string;
    category: string;
    status: string;
    priority: string;
    isSticky: boolean;
    dueDate: string | null;
    createdAt: string;
    updatedAt: string;
    user: { id: number; username: string };
    assignedUser: { id: number; username: string } | null;
    environment: { id: number; name: string; host: string } | null;
    comments: {
        id: number;
        content: string;
        createdAt: string;
        user: { id: number; username: string };
    }[];
};

type View = 'list' | 'create' | 'detail';

const Support = () => {
    const { t } = useTranslation();
    const { user } = useMainStore();
    const [view, setView] = useState<View>('list');
    const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

    const { data, loading, error, refetch } = useQuery(GET_MY_SUPPORT_TICKETS, {
        fetchPolicy: 'cache-and-network',
    });

    const tickets: SupportTicket[] = data?.mySupportTickets ?? [];

    const openTicket = (id: number) => {
        setSelectedTicketId(id);
        setView('detail');
    };

    const goToList = () => {
        setView('list');
        setSelectedTicketId(null);
        refetch();
    };

    return (
        <div className="pb-[40px] max-w-[900px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-[30px]">
                <div>
                    <h1 className="text-[28px] md:text-[36px] font-extrabold text-white tracking-tight">
                        {t('support.title')}
                    </h1>
                    <p className="text-white/30 text-[13px] font-medium mt-[4px]">
                        {t('support.subtitle')}
                    </p>
                </div>
                {view === 'list' ? (
                    <GButton
                        variant="primary"
                        className="py-[10px] px-[20px] rounded-[12px]"
                        onClick={() => setView('create')}
                    >
                        {t('support.newTicket')}
                    </GButton>
                ) : (
                    <GButton
                        variant="secondary"
                        className="py-[10px] px-[20px] rounded-[12px]"
                        onClick={goToList}
                    >
                        {t('support.backToTickets')}
                    </GButton>
                )}
            </div>

            {/* Error */}
            {view === 'list' && error && (
                <div className="mb-[20px] px-4 py-3 rounded-[12px] text-[14px] font-semibold bg-red-500/20 text-red-400 border border-red-500/30 animate-[fadeIn_0.3s_ease-out]">
                    {t('support.loadError')}
                </div>
            )}

            {/* Views */}
            {view === 'list' && (
                <TicketList
                    tickets={tickets}
                    loading={loading}
                    onSelect={openTicket}
                    onCreateNew={() => setView('create')}
                />
            )}

            {view === 'create' && (
                <CreateTicketForm
                    onSuccess={(ticket: SupportTicket) => openTicket(ticket.id)}
                    onCancel={goToList}
                />
            )}

            {view === 'detail' && selectedTicketId && (
                <TicketDetail
                    ticketId={selectedTicketId}
                    onBack={goToList}
                />
            )}
        </div>
    );
};

export default Support;
